import {
  Avatar,
  Banner,
  BlockStack,
  Box,
  Button,
  Card,
  Divider,
  IndexTable,
  InlineGrid,
  InlineStack,
  ResourceItem,
  ResourceList,
  Text,
  TextField,
  useIndexResourceState,
} from '@shopify/polaris'
import { DeleteIcon, PlusIcon } from '@shopify/polaris-icons'
import React, { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { notFoundImage } from '../../../../../assets'

const PAGE_SIZE = 5

function ObjectRecommendation({ objects, setObjects, error, setError }) {
  const { t } = useTranslation()
  return (
    <Card padding={'0'}>
      <Box padding={'400'} id='recommendation-object'>
        <BlockStack gap={'100'}>
          <Text as="strong">
            {t(
              'Section.Edit.Sections.Recommendation.ObjectRecommendation.title',
            )}
          </Text>
          <Text>
            {t(
              'Section.Edit.Sections.Recommendation.ObjectRecommendation.description',
            )}
          </Text>
        </BlockStack>
      </Box>
      <Divider />
      {objects.map((object, index) => (
        <>
          <ObjectTable
            onProductDelete={(selectedIds) => {
              const products = object.products.filter((p, idx) => {
                // Check if this is a deleted product placeholder being deleted
                if (selectedIds.includes(`deleted-${idx}`)) {
                  return false // Remove it
                }
                // Check if this is a regular product being deleted
                if (p !== null && selectedIds.includes(p.id)) {
                  return false // Remove it
                }
                return true // Keep it
              })
              const newObjects = [...objects]
              newObjects[index] = { ...object, products }
              setObjects(newObjects)
            }}
            onChange={(newObject) => {
              const newObjects = [...objects]
              newObjects[index] = { ...object, ...newObject }
              setObjects(newObjects)
            }}
            object={object}
            products={object.products}
            url={object.url}
            onRemove={() => {
              const newObjects = [...objects]
              newObjects.splice(index, 1)
              setObjects(newObjects)
            }}
            urls={objects.map((o) => o.url).filter((u, i) => i !== index)}
            error={error?.[index]}
            onError={(newError) => {
              setError(newError)
            }}
          />
          <Divider />
        </>
      ))}
      <Box padding={'400'}>
        <Button
          icon={PlusIcon}
          onClick={() => {
            setObjects([...objects, { url: '', products: [] }])
          }}
        >
          {t(
            'Section.Edit.Sections.Recommendation.ObjectRecommendation.addRecommendation',
          )}
        </Button>
      </Box>
    </Card>
  )
}

export default ObjectRecommendation

function ObjectTable({
  onProductDelete,
  onChange,
  object,
  products,
  url,
  onRemove,
  urls,
  error: errorProp,
  onError,
}) {
  const [error, setError] = useState(null)
  const [isDuplicate, setIsDuplicate] = useState(false)
  const [page, setPage] = useState(1)
  const [selectedItems, setSelectedItems] = useState([])
  const { t } = useTranslation()


  // Transform products array to replace null with placeholder objects
  // This prevents ResourceList from throwing errors when accessing properties on null
  const transformedProducts = products.map((product, index) => {
    if (product._deleted) {
      return {
        id: product.id,
        _isDeleted: true,
        _originalIndex: index
      }
    }
    return product
  })

  useEffect(() => {
    const regex = /^\/(?:[a-zA-Z0-9\-._~%!$&'()*+,;=:@/])*$/
    if (regex.test(url) || url.length === 0) {
      setError(null)
    } else {
      setError(
        t(
          'Section.Edit.Sections.Recommendation.ObjectRecommendation.invalidUrl',
        ),
      )
    }
  })

  return (
    <>
      <Box padding={'400'}>
        <Text>
          {t(
            'Section.Edit.Sections.Recommendation.ObjectRecommendation.addUrlLabel',
          )}
        </Text>
        <InlineGrid columns={'1fr auto'} gap={'200'} alignItems="start">
          <TextField
            helpText={t(
              'Section.Edit.Sections.Recommendation.ObjectRecommendation.helpText',
            )}
            error={
              error ?? isDuplicate
                ? t(
                    'Section.Edit.Sections.Recommendation.ObjectRecommendation.enterUniqueUrls',
                  )
                : null
            }
            value={url}
            onChange={(value) => {
              const count = urls.filter((u) => u === value).length
              if (count > 0) {
                setIsDuplicate(true)
                onError(
                  t(
                    'Section.Edit.Sections.Recommendation.ObjectRecommendation.enterUniqueUrls',
                  ),
                )
              } else {
                setIsDuplicate(false)
                onError(null)
              }
              onChange({
                url: value,
                products,
                id: object?.id,
              })
            }}
          />
          <div
            style={{
              display: 'flex',
              flexDirection: 'row',
              justifyContent: 'flex-end',
              gap: 'var(--p-space-200)',
            }}
          >
            <Button
              icon={DeleteIcon}
              size="micro"
              onClick={() => {
                onRemove()
              }}
            />
            <Button
              onClick={async () => {
                try {
                  const prods = await shopify.resourcePicker({
                    type: 'product',
                    selectionIds: products.map((p) => {
                      if(!p) return null;
                      return {
                      id: 'gid://shopify/Product/' + p.id,
                    }}).filter(Boolean),
                    multiple: 20,
                    filter: {
                      variants: false,
                      archived: false,
                    },
                  })
                  if (prods) {
                    onChange({
                      url,
                      products: prods.map((p) => ({
                        images: p.featuredImage?.url
                          ? [p.featuredImage?.url]
                          : [],
                        title: p.title,
                        vendor: p.vendor,
                        handle: p.handle,
                        id: parseInt(
                          p.id.replace('gid://shopify/Product/', ''),
                        ),
                      })),
                      id: object?.id,
                    })
                  }
                } catch (e) {
                  window.shopify.toast.show(t('noProductUpdate'), {
                    isError: true,
                  })
                }
              }}
              disabled={error || url.length === 0}
            >
              {t(
                'Section.Edit.Sections.Recommendation.ObjectRecommendation.addCta',
              )}
            </Button>
          </div>
        </InlineGrid>
      </Box>
      <Divider />
      <ResourceList
        resourceName={{
          singular: 'product',
          plural: 'products',
        }}
        items={transformedProducts.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE)}
        renderItem={(product, _, index) => renderItem(product, _, index, t)}
        selectedItems={selectedItems}
        onSelectionChange={setSelectedItems}
        resolveItemId={resolveItemIds}
        pagination={{
          hasPrevious: page > 1,
          hasNext: transformedProducts.length > page * PAGE_SIZE,
          onPrevious: () => setPage((prev) => prev - 1),
          onNext: () => setPage((prev) => prev + 1),
        }}
        promotedBulkActions={[
          {
            content: t(
              'Section.Edit.Sections.Recommendation.ObjectRecommendation.deleteCta',
            ),
            onAction: () => {
              onProductDelete(selectedItems)
              setSelectedItems([])
            },
            icon: DeleteIcon,
          },
        ]}
        emptyState={
          <Box padding={'400'}>
            {' '}
            <InlineStack alignment="center">
              <Text>
                {t('Section.Details.ObjectRecommendation.emptyState')}
              </Text>
            </InlineStack>
          </Box>
        }
      />
    </>
  )
}

function renderItem(product, _, index, t) {
  // Handle deleted products (marked with _isDeleted flag)
  if (product._isDeleted) {
    const media = (
      <Avatar
        size="sm"
        name={t('Section.Edit.Sections.Recommendation.ObjectRecommendation.deletedProduct')}
        source={notFoundImage}
      />
    )

    return (
      <ResourceItem id={product.id} key={product.id} media={media} sortOrder={index}>
        <BlockStack>
          <Text as="strong" tone="critical">
            {t('Section.Edit.Sections.Recommendation.ObjectRecommendation.deletedProduct')}
          </Text>
          <Text tone="subdued">
            {t('Section.Edit.Sections.Recommendation.ObjectRecommendation.deletedProductMessage')} <br/> ID: {product.id}
          </Text>
        </BlockStack>
      </ResourceItem>
    )
  }

  const media = (
    <Avatar
      size="sm"
      name={product.title}
      source={
        product.images?.[0] ||
        notFoundImage
      }
    />
  )

  return (
    <ResourceItem id={product.id} media={media} sortOrder={index}>
      <BlockStack>
        <Text as="strong">{product.title}</Text>
        <Text>ID: {product.id}</Text>
      </BlockStack>
    </ResourceItem>
  )
}

function resolveItemIds(item) {
  // All items now have an id property (including deleted products with placeholder objects)
  return item.id
}
